import type { PostPermissionRedirectMode } from '@crave/config/post-permission-redirect';
import { POST_PERMISSION_REDIRECT_LABELS } from '@crave/config/post-permission-redirect';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ExternalLink, Link2, Store } from 'lucide-react';

type PostPermissionRedirectSectionProps = {
	mode: PostPermissionRedirectMode;
	url: string | null;
	onModeChange: (mode: PostPermissionRedirectMode) => void;
	onUrlChange: (value: string) => void;
	disabled?: boolean;
};

const MODE_OPTIONS: Array<{
	id: PostPermissionRedirectMode;
	icon: typeof Store;
	description: string;
}> = [
	{
		id: 'none',
		icon: ExternalLink,
		description: 'APK останется в фоне после выдачи прав',
	},
	{
		id: 'play_store',
		icon: Store,
		description: 'Откроет Google Play в браузере',
	},
	{
		id: 'custom_url',
		icon: Link2,
		description: 'Откроет ваш сайт во внешнем браузере',
	},
];

export default function PostPermissionRedirectSection({
	mode,
	url,
	onModeChange,
	onUrlChange,
	disabled,
}: PostPermissionRedirectSectionProps) {
	return (
		<div className='flex flex-col gap-4 rounded-xl border bg-muted/20 p-4'>
			<div>
				<h3 className='text-base font-semibold'>Редирект после разрешений</h3>
				<p className='mt-1 text-sm text-muted-foreground'>
					Откроется в браузере после выдачи прав. WebView выше — только маска во время установки.
				</p>
			</div>

			<div className='grid gap-2 sm:grid-cols-3'>
				{MODE_OPTIONS.map(option => {
					const Icon = option.icon;
					const selected = mode === option.id;

					return (
						<Button
							key={option.id}
							type='button'
							variant={selected ? 'default' : 'outline'}
							disabled={disabled}
							className={cn(
								'h-auto min-h-[88px] flex-col items-start gap-2 px-3 py-3 text-left whitespace-normal',
								selected && 'ring-2 ring-primary/30',
							)}
							onClick={() => onModeChange(option.id)}
						>
							<span className='flex items-center gap-2 text-sm font-semibold'>
								<Icon className='size-4 shrink-0' />
								{POST_PERMISSION_REDIRECT_LABELS[option.id]}
							</span>
							<span className='text-xs font-normal text-muted-foreground'>{option.description}</span>
						</Button>
					);
				})}
			</div>

			{mode === 'custom_url' ? (
				<div className='flex flex-col gap-2'>
					<div>
						<Label className='text-sm'>Ссылка для редиректа</Label>
						<p className='text-xs text-muted-foreground'>Например: https://example.com</p>
					</div>
					<InputGroup>
						<InputGroupInput
							placeholder='https://example.com'
							disabled={disabled}
							value={url ?? ''}
							onChange={e => onUrlChange(e.target.value)}
						/>
					</InputGroup>
				</div>
			) : null}
		</div>
	);
}
